import React, { Dispatch, useState } from 'react';
import {
  Button,
  Checkbox,
  EmptyState,
  EmptyStateBody,
  EmptyStateHeader,
  List,
  ListItem,
  Modal,
  ModalVariant,
} from '@patternfly/react-core';
import { Infobox } from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import ComponentError from 'components/Error/ComponentError';
import PremiumSessionLegends from 'components/PremiumSessionLegends';
import { handleError } from 'services/ErrorService';
import { extractFromSlug } from 'helpers/utils';

import {
  PremiumSession,
  PremiumSessionAggregate,
  PremiumSessionCollateral,
  PremiumSessionUserFacingStatus,
} from 'types/premiumSession';
import usePremiumSessionAggregateInfo from 'hooks/usePremiumSessionAggregateInfo';
import classNames from 'classnames';
import { InfoCircleIcon } from '@patternfly/react-icons';
import { Table, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import { DateTime } from 'luxon';
import useMobxStores from 'hooks/useMobxStores';
import { PremiumSessionCard } from 'components/OfferingCards';
import usePremiumSessionLegends from './usePremiumSessionLegends';

import './styles.scss';

type Props = {
  /** Collateral data for the premium session (objectives, title, etc.) */
  coursePremiumSessionCollateral?: PremiumSessionCollateral;
  /** Title of the course/offering, used in modal confirmation */
  courseTitle?: string;
  /** When true, shows objectives from collateral above the session list */
  showObjectivesFromCollateral?: boolean;
  /** Controls visibility of the bulk enrollment confirmation modal */
  isModalOpen: boolean;
  /** Handler for Include checkbox; receives the session and returns change handler */
  onCardSelect: (
    session: PremiumSession,
  ) => (_: React.FormEvent<HTMLInputElement>, checked: boolean) => void;
  /** Called when user confirms bulk enrollment in the modal */
  onConfirmEnrollment: () => void;
  /** Aggregate of premium sessions for the offering (content + scheduled sessions) */
  premiumSessionAggregate?: PremiumSessionAggregate;
  /** Set of doc_ids for sessions selected for bulk scheduling */
  selectedPremiumSessions: Set<string>;
  /** When true, shows Include checkboxes for schedulable sessions */
  selectionMode?: boolean;
  /** Setter to open/close the confirmation modal */
  setIsModalOpen: Dispatch<React.SetStateAction<boolean>>;
  /** Slugs of versions that have premium sessions (for empty state message) */
  slugsWithPremiumSessions?: string[];
};

/**
 * Renders a list of premium (live) sessions for an offering, with bulk scheduling support.
 *
 * Shows objectives (optional), quota info, scheduling details, legend filters, and
 * session cards. Used on both Course Landing and Premium Landing pages.
 *
 * @see usePremiumSessionsHandlers - provides selection state and handlers
 */
const PremiumSessionsList = ({
  coursePremiumSessionCollateral,
  courseTitle,
  showObjectivesFromCollateral = false,
  isModalOpen,
  onCardSelect,
  onConfirmEnrollment,
  premiumSessionAggregate,
  selectedPremiumSessions,
  selectionMode,
  setIsModalOpen,
  slugsWithPremiumSessions = [],
}: Props) => {
  const [selectedStatus, setSelectedStatus] =
    useState<PremiumSessionUserFacingStatus>();
  const { t } = useTranslation();
  const { userStore } = useMobxStores();

  const { extraSessionInfoMap } = usePremiumSessionAggregateInfo({
    premiumSessionAggregate,
  });
  const legendsData = usePremiumSessionLegends(extraSessionInfoMap);

  if (!premiumSessionAggregate) {
    return (
      <div className="premium-sessions">
        <EmptyState>
          <EmptyStateHeader
            titleText={t(
              'There are no live sessions for this version of the course.',
            )}
            headingLevel="h4"
          />
          <EmptyStateBody>
            {t(
              'Select one of the following versions to see available live sessions: {{versionList}}.',
              {
                versionList: slugsWithPremiumSessions
                  .map((slug) => extractFromSlug(slug).version)
                  .join(', '),
              },
            )}
          </EmptyStateBody>
        </EmptyState>
      </div>
    );
  }

  const allowed =
    userStore.entitlementEnablements?.quotas?.sessions_scheduled?.allowed ?? 0;
  const used =
    userStore.entitlementEnablements?.quotas?.sessions_scheduled?.used ?? 0;

  return (
    <div className="premium-sessions">
      {showObjectivesFromCollateral && (
        <div>
          <b className="premium-sessions__objectives-title">
            {t('Objectives')}
          </b>
          <List>
            {coursePremiumSessionCollateral?.objectives.map((objective) => (
              <ListItem key={objective}>{objective}</ListItem>
            ))}
          </List>
        </div>
      )}

      <span>
        <Trans
          components={{ bold: <b /> }}
          defaults="You've scheduled <bold>{{used}} out of {{allowed}}</bold> sessions included with your subscription."
          values={{ allowed, used }}
        />
      </span>

      <Infobox
        title={t('Session scheduling details')}
        className="premium-sessions__infobox"
        titleIcon={
          <InfoCircleIcon color="var(--pf-v5-global--info-color--100)" />
        }
        borderColor="var(--pf-v5-global--info-color--100)"
      >
        <List>
          <ListItem>
            <Trans>
              The duration of each session is <b>3 hours</b>.
            </Trans>
          </ListItem>

          <ListItem>
            <Trans>
              Your subscription includes{' '}
              <b>up to twenty session reservations</b>.
            </Trans>
          </ListItem>

          <ListItem>
            <Trans>
              Cancellations are accepted <b>up to two weeks</b> before the
              session&apos;s start date.
            </Trans>
          </ListItem>

          <ListItem>
            {t(
              'Missed sessions without timely cancellation will count toward your twenty allowed reservations.',
            )}
          </ListItem>

          <ListItem>
            {t(
              'Bulk scheduling selects all available sessions by default. Use the Include checkbox to adjust each session.',
            )}
          </ListItem>

          <ListItem>
            {t(
              'Please note that sessions can only be scheduled within the duration of your active subscription.',
            )}
          </ListItem>
        </List>
      </Infobox>

      <div
        className={classNames('premium-sessions__bulk-schedule-container', {
          active: selectionMode,
        })}
      >
        {selectionMode && (
          <h4 className="premium-sessions__bulk-schedule-title">
            {t('Bulk scheduling')}
          </h4>
        )}

        <PremiumSessionLegends
          amount={premiumSessionAggregate?.content.sessions.length}
          available={legendsData[PremiumSessionUserFacingStatus.Schedulable]}
          attended={legendsData[PremiumSessionUserFacingStatus.Attended]}
          interested={legendsData[PremiumSessionUserFacingStatus.Requested]}
          scheduled={legendsData[PremiumSessionUserFacingStatus.Enrolled]}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          unavailable={legendsData[PremiumSessionUserFacingStatus.Unavailable]}
        />

        <div className="premium-sessions__list">
          {premiumSessionAggregate?.content?.sessions?.reduce(
            (filteredValues: React.ReactNode[], sessionDefinition) => {
              const fullSessionSlug = `${premiumSessionAggregate.content.offering_slug}-${sessionDefinition.slug}`;

              const sessionInfo = extraSessionInfoMap.get(fullSessionSlug);
              if (!sessionInfo) return filteredValues;

              const { nextSchedulableSession, userFacingStatus } = sessionInfo;

              const selectable =
                selectionMode &&
                userFacingStatus === PremiumSessionUserFacingStatus.Schedulable;

              if (selectedStatus && userFacingStatus !== selectedStatus) {
                return filteredValues;
              }

              return [
                ...filteredValues,
                <PremiumSessionCard
                  className={classNames({ 'active-card': selectable })}
                  key={sessionDefinition.slug}
                  premiumSessionAggregate={premiumSessionAggregate}
                  hideAllActionButtons={selectable}
                  sessionDefinition={sessionDefinition}
                  sessionInfo={sessionInfo}
                >
                  {selectable && (
                    <Checkbox
                      label={t('Include')}
                      isChecked={selectedPremiumSessions.has(
                        nextSchedulableSession.doc_id,
                      )}
                      onChange={onCardSelect(nextSchedulableSession)}
                      id={`checkbox-${sessionDefinition.slug}`}
                    />
                  )}
                </PremiumSessionCard>,
              ];
            },
            [],
          )}
        </div>

        <Modal
          className="premium-sessions__modal"
          variant={ModalVariant.medium}
          title={t(
            `Are you sure you want to schedule the sessions below in {{title}}?`,
            { title: courseTitle },
          )}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          actions={[
            <Button
              key="confirm"
              variant="primary"
              onClick={onConfirmEnrollment}
            >
              {t('Confirm')}
            </Button>,
            <Button
              key="cancel"
              variant="link"
              onClick={() => setIsModalOpen(false)}
            >
              {t('Cancel')}
            </Button>,
          ]}
        >
          <Table variant="compact">
            <Thead>
              <Tr>
                <Th>{t('Session')}</Th>
                <Th>{t('Date')}</Th>
                <Th>{t('Time')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Array.from(selectedPremiumSessions).map((sessionId) => {
                const selectedSession = premiumSessionAggregate.sessions.find(
                  (session) => session.doc_id === sessionId,
                );

                const fullSessionSlug = selectedSession.premvt_session_slug;
                const sessionTitle = t('Session {{sessionNumber}}', {
                  sessionNumber: parseInt(
                    fullSessionSlug.match(/s(\d+)$/)?.[1] || '0',
                    10,
                  ),
                });

                return (
                  <Tr key={sessionId}>
                    <Td dataLabel={t('Session')}>{sessionTitle}</Td>
                    <Td dataLabel={t('Date')}>
                      {DateTime.fromISO(selectedSession.start_time).toFormat(
                        'MMM dd, yyyy',
                      )}
                    </Td>
                    <Td dataLabel={t('Time')}>
                      {DateTime.fromISO(selectedSession.start_time).toFormat(
                        'hh:mm a',
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Modal>
      </div>
    </div>
  );
};

export default withErrorBoundary(
  observer(PremiumSessionsList),
  ComponentError,
  handleError,
);
